import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter,
    HostBinding, Input, Output, ViewChild, forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Hora del día.
 *
 * Sustituye al `timepicker` de ngx-bootstrap, que traía su propio skin: tres
 * cajas de 32 px con flechas, fuera de la escala de control del sistema, y una
 * `AM`/`PM` que era un botón que había que pulsar para adivinar que alternaba.
 *
 * Decisiones:
 *
 * - **Dos campos de dos dígitos, no uno de cinco.** Con un solo campo hay que
 *   teclear los dos puntos, y en un teclado numérico de móvil no está.
 * - **Salto automático.** Al segundo dígito de la hora el foco pasa a los
 *   minutos; con Retroceso en unos minutos vacíos, vuelve. El usuario teclea
 *   `0830` sin tocar el ratón.
 * - **Meridiano como segmentado.** AM y PM se ven los dos a la vez: se lee cuál
 *   está activo sin pulsar nada. Con un botón que alterna, no.
 * - **El valor sigue siendo `Date`**, que es lo que ya guardaba el formulario y
 *   lo que consume `transformPayload`. Cambiarlo habría tocado el guardado.
 */
@Component({
    standalone: true,
    selector: 'dxc-time-field',
    templateUrl: './dxc-time-field.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DxcTimeFieldComponent), multi: true }],
})
export class DxcTimeFieldComponent implements ControlValueAccessor {

    @Input() invalid = false;
    @Input() ariaLabel = '';
    /** Id del campo de hora, para el `<label for>`. */
    @Input() fieldId = '';

    @Output() valueChange = new EventEmitter<Date | null>();

    @HostBinding('class.dxc-time') readonly hostClass = true;

    @ViewChild('hh') hhRef?: ElementRef<HTMLInputElement>;
    @ViewChild('mm') mmRef?: ElementRef<HTMLInputElement>;

    hours = '';
    minutes = '';
    meridiem: 'AM' | 'PM' = 'AM';
    disabled = false;

    #onChange: (value: Date | null) => void = () => { };
    #onTouched: () => void = () => { };

    constructor(private cdr: ChangeDetectorRef) { }

    // --- ControlValueAccessor ------------------------------------------------

    writeValue(value: Date | string | null) {
        const date = this.#toDate(value);
        if (!date) {
            this.hours = '';
            this.minutes = '';
            this.meridiem = 'AM';
        } else {
            const raw = date.getHours();
            this.meridiem = raw >= 12 ? 'PM' : 'AM';
            const twelve = raw % 12 === 0 ? 12 : raw % 12;
            this.hours = String(twelve).padStart(2, '0');
            this.minutes = String(date.getMinutes()).padStart(2, '0');
        }
        this.cdr.markForCheck();
    }

    registerOnChange(fn: (value: Date | null) => void) { this.#onChange = fn; }
    registerOnTouched(fn: () => void) { this.#onTouched = fn; }
    setDisabledState(disabled: boolean) { this.disabled = disabled; this.cdr.markForCheck(); }

    // --- Entrada -------------------------------------------------------------

    onHours(text: string) {
        this.hours = this.#digits(text, 2);
        // Un primer dígito mayor que 1 solo puede ser una hora de un dígito.
        if (this.hours.length === 1 && Number(this.hours) > 1) this.hours = this.hours.padStart(2, '0');
        if (this.hours.length === 2) {
            this.hours = String(this.#clamp(Number(this.hours), 1, 12)).padStart(2, '0');
            this.mmRef?.nativeElement.focus();
            this.mmRef?.nativeElement.select();
        }
        this.#emit();
    }

    onMinutes(text: string) {
        this.minutes = this.#digits(text, 2);
        if (this.minutes.length === 2) {
            this.minutes = String(this.#clamp(Number(this.minutes), 0, 59)).padStart(2, '0');
        }
        this.#emit();
    }

    /** Retroceso en unos minutos vacíos devuelve el foco a la hora. */
    onMinutesKeydown(event: KeyboardEvent) {
        if (event.key === 'Backspace' && !this.minutes) {
            this.hhRef?.nativeElement.focus();
            this.hhRef?.nativeElement.select();
        }
    }

    setMeridiem(meridiem: 'AM' | 'PM') {
        if (this.disabled || this.meridiem === meridiem) return;
        this.meridiem = meridiem;
        this.#emit();
    }

    onBlur() {
        // Al salir se normaliza lo escrito a medias: «8» pasa a «08».
        if (this.hours.length === 1) this.hours = this.hours.padStart(2, '0');
        if (this.minutes.length === 1) this.minutes = this.minutes.padStart(2, '0');
        this.#onTouched();
        this.#emit();
    }

    // --- Interno -------------------------------------------------------------

    #digits(text: string, max: number) { return (text || '').replace(/\D/g, '').slice(0, max); }
    #clamp(value: number, min: number, max: number) { return Math.min(max, Math.max(min, value)); }

    #toDate(value: Date | string | null): Date | null {
        if (!value) return null;
        if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
        const parsed = new Date(value);
        return isNaN(parsed.getTime()) ? null : parsed;
    }

    #emit() {
        const value = this.#compose();
        this.#onChange(value);
        this.valueChange.emit(value);
        this.cdr.markForCheck();
    }

    /**
     * Hora incompleta es hora sin valor: se emite `null` en vez de inventar los
     * minutos, o el formulario guardaría una ventana horaria que nadie escribió.
     */
    #compose(): Date | null {
        if (this.hours.length !== 2 || this.minutes.length !== 2) return null;

        const twelve = this.#clamp(Number(this.hours), 1, 12);
        const minutes = this.#clamp(Number(this.minutes), 0, 59);
        const hours = this.meridiem === 'PM'
            ? (twelve === 12 ? 12 : twelve + 12)
            : (twelve === 12 ? 0 : twelve);

        const date = new Date();
        date.setHours(hours, minutes, 0, 0);
        return date;
    }
}
